import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import type { RootReduxState } from 'app/shared/flux/reducers';
import { useCostAndFeesWithCache } from 'app/shared/modules/hdp/CostsAndFees/hooks/useCostAndFeesCache';
import {
  type Amount,
  type CostsAndFees,
  type Fee,
  type FloorPlan,
  type SelectedFeeOption,
  type SelectedFeeOptions,
  type Selector,
  type UngroupedUnit,
  type UnitSelectorWithAdditional,
} from 'app/types/costAndFees.type';
import {
  getDisplayAmountForCurrency,
  getSelectedOptionFrom,
  getSelectedOptions,
  updateSelectedOptionFor,
} from '../utils';
import { useCostCalculation } from '../hooks/useCostCalculation';

interface UnitOptionsGrouped {
  [key: number]: Array<UnitSelectorWithAdditional>;
}

interface CostsAndFeesContextInterface {
  costsAndFees?: CostsAndFees;
  calculatedCostsAndFees: CostsAndFees | null;
  SelectedFeeOptions: SelectedFeeOptions;
  getSelectedOption: (fee: Fee) => SelectedFeeOption | undefined;
  updateSelectedOption: (fee: Fee, option?: SelectedFeeOption | null) => void;
  setSelectedOptions: (options: SelectedFeeOptions) => void;
  selectorOverrides: Array<Selector>;
  setSelectorOverrides: (overrides: Array<Selector>) => void;
  floorPlans?: Array<FloorPlan>;
  ungroupedUnits?: Array<UngroupedUnit>;
  getDisplayAmount: (amount?: Amount | null) => string;
  unitOptionsGrouped: UnitOptionsGrouped;
  isWaitlisted: boolean;
  subAppPageLocation?: string;
}

const CostsAndFeesContext = createContext<
  Omit<CostsAndFeesContextInterface, 'costsAndFees' | 'floorPlans' | 'ungroupedUnits' | 'subAppPageLocation'>
>({
  calculatedCostsAndFees: null,
  SelectedFeeOptions: {},
  getSelectedOption: () => undefined,
  updateSelectedOption: () => {},
  setSelectedOptions: () => {},
  selectorOverrides: [],
  setSelectorOverrides: () => {},
  getDisplayAmount: () => '',
  unitOptionsGrouped: {},
  isWaitlisted: false,
});

const useCostsAndFeesContext = () => useContext(CostsAndFeesContext);

interface CostsAndFeesContextProviderProps {
  children: React.ReactNode;
  floorPlans?: Array<FloorPlan>;
  ungroupedUnits?: Array<UngroupedUnit>;
  isWaitlisted?: boolean;
  subAppPageLocation?: string;
}

const CostsAndFeesContextProvider = ({
  children,
  floorPlans,
  isWaitlisted = false,
  ungroupedUnits,
}: CostsAndFeesContextProviderProps) => {
  const { currentListing } = useSelector((state: RootReduxState) => state.currentListingDetails);
  const { aliasEncoded } = currentListing ?? {};
  const { listingCostAndFees: costsAndFees } = useCostAndFeesWithCache(aliasEncoded ?? '');

  const [selectorOverrides, setSelectorOverrides] = useState<Array<Selector>>([]);
  const [SelectedFeeOptions, setSelectedFeeOptions] = useState<SelectedFeeOptions>({});
  const [unitOptionsGrouped, setUnitOptionsGrouped] = useState<UnitOptionsGrouped>({});

  const calculatedCostsAndFees = useCostCalculation({
    costsAndFees: costsAndFees ?? undefined,
    selectorOverrides,
    SelectedFeeOptions,
  });

  useEffect(() => {
    const storedSelectedFeeOptions = getSelectedOptions();
    setSelectedFeeOptions(storedSelectedFeeOptions);
  }, []);

  useEffect(() => {
    if (costsAndFees?.selectors?.units) {
      const groupedSelectors = costsAndFees.selectors.units.reduce((acc: UnitOptionsGrouped, selector) => {
        let unitOptionToUpdate: UnitSelectorWithAdditional | null = null;
        if (floorPlans) {
          const matchingFloorPlan = floorPlans.find((fp) => fp.zpid === selector.unitZpid);
          if (matchingFloorPlan) {
            const exactUnit = matchingFloorPlan.units?.find((u) => u.zpid === selector.unitZpid);
            unitOptionToUpdate = {
              ...selector,
              availableFrom: exactUnit?.availableFrom ?? matchingFloorPlan.availableFrom,
              isRepresentative: !exactUnit,
              numBaths: matchingFloorPlan.baths,
              floorplanZpid: matchingFloorPlan.zpid,
              studentHousingType: exactUnit?.studentHousingType,
            };
          } else {
            floorPlans.forEach((fp) => {
              const matchingUnit = fp.units?.find((u) => u.zpid === selector.unitZpid);
              if (matchingUnit) {
                unitOptionToUpdate = {
                  ...selector,
                  studentHousingType: matchingUnit.studentHousingType,
                  availableFrom: matchingUnit.availableFrom,
                  isRepresentative: false,
                  numBaths: fp.baths,
                  floorplanZpid: fp.zpid,
                };
              }
            });
          }
        } else if (ungroupedUnits) {
          const matchingUnit = ungroupedUnits.find((u) => u.zpid === selector.unitZpid);
          if (matchingUnit) {
            unitOptionToUpdate = {
              ...selector,
              availableFrom: matchingUnit.availableFrom,
              isRepresentative: false,
              numBaths: matchingUnit.baths,
              hdpUrl: matchingUnit.hdpUrl,
            };
          }
        }

        if (unitOptionToUpdate) {
          if (!acc[selector.numBeds]) {
            acc[selector.numBeds] = [];
          }
          acc[selector.numBeds].push(unitOptionToUpdate);
        }
        return acc;
      }, {});
      setUnitOptionsGrouped(groupedSelectors);
    }
  }, [costsAndFees, floorPlans, ungroupedUnits]);

  const getSelectedFeeOption = useMemo(() => getSelectedOptionFrom(SelectedFeeOptions), [SelectedFeeOptions]);

  const updateSelectedOption = useCallback(
    (fee: Fee, option?: SelectedFeeOption | null) => {
      const updateFunc = updateSelectedOptionFor(SelectedFeeOptions);
      const newSelectedFeeOptions = updateFunc(fee, option);
      setSelectedFeeOptions(newSelectedFeeOptions);
    },
    [SelectedFeeOptions],
  );

  const displayAmountFormatter = useMemo(() => getDisplayAmountForCurrency(), []);

  return (
    <CostsAndFeesContext.Provider
      value={{
        calculatedCostsAndFees,
        SelectedFeeOptions,
        getSelectedOption: getSelectedFeeOption,
        updateSelectedOption,
        setSelectedOptions: setSelectedFeeOptions,
        selectorOverrides,
        setSelectorOverrides,
        getDisplayAmount: displayAmountFormatter,
        unitOptionsGrouped,
        isWaitlisted,
      }}
    >
      {children}
    </CostsAndFeesContext.Provider>
  );
};

export { CostsAndFeesContext, CostsAndFeesContextProvider, useCostsAndFeesContext };
